import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { investorService, ConsentItem } from '../../../services/investor.service';
import { profileService } from '../../../services/profile.service';
import { sowService } from '../../../services/sow.service';
import { authenticationService } from '../../../services/authentication.service';
import { SowAgreementModal } from './SowAgreementModal';
import { FiChevronLeft } from 'react-icons/fi';
import { BsCheckCircleFill, BsXCircleFill, BsEyeFill } from 'react-icons/bs';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import '../InvestorProfile/InvestorProfile.scss';
import './MyConsents.scss';

const ChevronLeftIcon = FiChevronLeft as any;
const CheckCircleIcon = BsCheckCircleFill as any;
const XCircleIcon = BsXCircleFill as any;
const EyeIcon = BsEyeFill as any;

/**
 * My Consents (Consent Centre detail).
 *
 * Read-only summary lives on the dashboard; clicking it lands here. Shows every consent
 * (Consent / Scope / Status / Action). Each row's action runs its own flow:
 *  - Statement of Work: Agree (records agreement → unlocks the onboarding journey) / Revoke
 *    (re-blocks the journey).
 *  - Marketing / WhatsApp / Privacy / Platform Terms: Activate / Revoke toggles.
 */
/**
 * Module-level cache so returning to Consents renders instantly from the last load
 * (stale-while-revalidate) instead of a spinner + cold fetch. Lives for the app session.
 */
let consentsCache: ConsentItem[] | null = null;

export const MyConsents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Spinner only on the very first load; on return, show cached items immediately.
  const [loading, setLoading] = useState(!consentsCache);
  const [items, setItems] = useState<ConsentItem[]>(consentsCache ?? []);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [showSowModal, setShowSowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await investorService.getDashboard();
      const list = data?.consentCenter || [];
      setItems(list);
      consentsCache = list;
    } catch (e) {
      console.error('[MyConsents] load failed', e);
      toast.error('Failed to load consents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Sidebar deep link: /investor/consents?open=sow lands straight on the SOW agreement.
  useEffect(() => {
    if (searchParams.get('open') === 'sow') setShowSowModal(true);
  }, [searchParams]);

  const handleView = async (item: ConsentItem) => {
    const key = item.key;
    if (!key) return;

    if (key === 'sow') {
      setShowSowModal(true);
      return;
    }

    if (key === 'privacy' || key === 'privacyPolicy') {
      window.open('/privacy-policy', '_blank');
      if ((item.action || '').toUpperCase() === 'ACTIVATE') {
        await triggerActivate(item);
      }
      return;
    }

    if (key === 'platformTerms' || key === 'terms') {
      window.open('/service-agreement', '_blank');
      if ((item.action || '').toUpperCase() === 'ACTIVATE') {
        await triggerActivate(item);
      }
      return;
    }

    // For other consents (marketing, whatsapp, etc.)
    if ((item.action || '').toUpperCase() === 'ACTIVATE') {
      await triggerActivate(item);
    } else {
      toast.info(`${item.consent || 'Consent'} is already active.`);
    }
  };

  const triggerActivate = async (item: ConsentItem) => {
    const key = item.key;
    if (!key) return;

    setBusyKey(key);
    try {
      await profileService.updateConsent(key, 'activate');
      toast.success('Consent updated.');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Activation failed');
    } finally {
      setBusyKey(null);
    }
  };

  // UX task D (2026-08-23): the revoke flow, DEFINED per consent type. Every revocation is
  // explicit (labelled button → consequence modal → confirm), recorded with date/time in the
  // consent ledger by the existing endpoints. Consequences by type:
  //  - sow:            re-closes every onboarding journey until agreed again
  //  - privacy/terms:  foundational — account processing pauses until re-accepted
  //  - marketing:      stops marketing communications only
  //  - whatsapp:       stops WhatsApp updates only (email unaffected)
  const REVOKE_CONSEQUENCE: Record<string, string> = {
    sow: 'Revoking your Statement of Work re-closes your onboarding journeys — you cannot continue any journey until you agree to it again.',
    privacy: 'The Privacy Policy consent is required for Facilon to process your information. Revoking it pauses your ongoing applications until you accept it again.',
    privacyPolicy: 'The Privacy Policy consent is required for Facilon to process your information. Revoking it pauses your ongoing applications until you accept it again.',
    platformTerms: 'The Platform Terms are required to use Facilon. Revoking them restricts your account until you accept them again.',
    terms: 'The Platform Terms are required to use Facilon. Revoking them restricts your account until you accept them again.',
    marketing: 'You will stop receiving marketing communications on both email and mobile. Your applications and journeys are not affected.',
  };
  const [revokeItem, setRevokeItem] = useState<ConsentItem | null>(null);

  // Flow-1.pdf point 1: Privacy Consent / Platform Terms are foundational. Revoking them runs a
  // two-step AGREE/DISAGREE flow and, on AGREE, makes the account inoperative (backend) + logs out.
  const FOUNDATIONAL_KEYS = ['privacy', 'privacyPolicy', 'platformTerms', 'terms'];
  const isFoundational = (key?: string) => !!key && FOUNDATIONAL_KEYS.includes(key);
  const [deactivateItem, setDeactivateItem] = useState<ConsentItem | null>(null);
  const [deactivateStep, setDeactivateStep] = useState<1 | 2>(1);
  const [deactivating, setDeactivating] = useState(false);

  const triggerRevoke = (item: ConsentItem) => {
    if (!item.key) return;
    if (isFoundational(item.key)) {
      setDeactivateStep(1);
      setDeactivateItem(item);
      return;
    }
    setRevokeItem(item);
  };

  const confirmDeactivate = async () => {
    const item = deactivateItem;
    const key = item?.key;
    if (!key) return;
    setDeactivating(true);
    try {
      // Backend flips the account inoperative + emails the user and Facilon.
      await profileService.updateConsent(key, 'revoke');
      toast.success('Your account has been deactivated. You will now be signed out.');
      // The account is inoperative, so the current session is no longer valid — sign out.
      let logoutUrl: string | null = null;
      try {
        logoutUrl = await authenticationService.getAzureB2CLogoutUrl();
      } catch {
        logoutUrl = null;
      }
      authenticationService.logout();
      window.location.href = logoutUrl || '/login';
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Failed to deactivate account');
      setDeactivating(false);
      setDeactivateItem(null);
    }
  };

  const confirmRevoke = async () => {
    const item = revokeItem;
    setRevokeItem(null);
    const key = item?.key;
    if (!key) return;

    setBusyKey(key);
    try {
      if (key === 'sow') {
        await sowService.revokeMe();
        toast.success('Statement of Work revoked.');
      } else {
        await profileService.updateConsent(key, 'revoke');
        toast.success('Consent updated.');
      }
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Revocation failed');
    } finally {
      setBusyKey(null);
    }
  };

  const handleSowAgree = async () => {
    setBusyKey('sow');
    try {
      await sowService.agreeMe();
      toast.success('Statement of Work agreed — you can now continue your journey.');
      setShowSowModal(false);
      // Same destination as the in-journey prompt (user 2026-09-10): agreeing the SOW lands the
      // investor on the dashboard, where their journeys are, rather than leaving them on the
      // Consent Centre — which is what the toast already tells them to do next.
      navigate('/investor/dashboard');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Failed to record agreement');
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <div className="facilon-dashboard-wrapper">
        <main className="container-fluid dashboard-container-main p-0">
          <div className="loading-container" style={{ textAlign: 'center', padding: '100px 0' }}>
            <div className="spinner"></div>
            <p>Loading consents...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="facilon-dashboard-wrapper">
      <main className="container-fluid dashboard-container-main p-0">
        <div className="investor-profile dsr-page">

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 shadow-sm">
            {/* Blue Header Banner */}
            <div className="p-3 flex justify-between items-center text-white" style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}>
              <div>
                <h2 className="m-0 text-base font-bold text-white tracking-tight">Consent Centre</h2>
                <p className="m-0 text-[11.5px] font-normal text-white/80 mt-0.5">Manage your consents and Statement of Work agreements.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center bg-white/10 border border-white/20 text-white text-[11px] font-semibold h-[30px] px-3 rounded-md transition-colors hover:bg-white/20 hover:border-white/30 cursor-pointer text-decoration-none"
                  onClick={() => navigate('/investor/dashboard')}
                >
                  <ChevronLeftIcon size={12} className="me-1 flex-shrink-0" />
                  Back
                </button>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-3 bg-white">
              <div className="bg-transparent border-0 rounded-none mb-0 pt-2.5 p-0 bg-white" style={{ padding: '15px' }}>
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-[9px] font-bold text-slate-400 uppercase tracking-wider border-b border-[#e2e8f0]">
                        <th className="pb-3.5 px-2">Consent</th>
                        <th className="pb-3.5 px-2">Scope</th>
                        <th className="pb-3.5 px-2">Status</th>
                        <th className="pb-3.5 px-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e2e8f0]">
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-4 px-2 text-center text-[12px] text-slate-400">
                            No consent records available.
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => {
                          const isStatusActive = (item.status || '').toLowerCase() === 'active';

                          return (
                            <tr
                              key={item.key || item.consent}
                              className="hover:bg-slate-50/50 transition-colors"
                            >
                              <td className="p-2 text-[12px]">
                                <span className="font-bold text-slate-800">{item.consent || '-'}</span>
                              </td>
                              <td className="p-2 text-[12px] text-slate-600">{item.scope || '-'}</td>
                              <td className="p-2 text-[12px]">
                                {isStatusActive ? (
                                  <span className="inline-flex items-center gap-1 text-[8.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#355f69] text-white border border-[#355f69]/20">
                                    <CheckCircleIcon size={9.5} className="flex-shrink-0" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[8.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200">
                                    <XCircleIcon size={9.5} className="flex-shrink-0" />
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td className="p-2 text-[12px] text-right">
                                <div className="inline-flex items-center gap-1.5 justify-end w-full">
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id={`tooltip-view-${item.key || item.consent}`} className="text-[10px]">View/Accept Consent</Tooltip>}
                                  >
                                    <button
                                      type="button"
                                      className="inline-flex items-center justify-center w-[28px] h-[28px] bg-transparent border border-gray-200 hover:border-[#3e6f7c] hover:bg-[#3e6f7c]/5 text-[#3e6f7c] disabled:opacity-35 disabled:cursor-not-allowed rounded-md transition-all cursor-pointer"
                                      disabled={busyKey === item.key}
                                      onClick={() => handleView(item)}
                                    >
                                      <EyeIcon size={12} className="flex-shrink-0" />
                                    </button>
                                  </OverlayTrigger>

                                  {/* UX task D: labelled Revoke button (delete icon removed) */}
                                  <button
                                    type="button"
                                    className="inline-flex items-center px-2.5 h-[28px] bg-transparent border border-gray-200 hover:border-red-500 hover:bg-red-50 text-red-500 text-[11px] font-semibold disabled:opacity-35 disabled:cursor-not-allowed rounded-md transition-all cursor-pointer"
                                    disabled={busyKey === item.key || !isStatusActive}
                                    onClick={() => triggerRevoke(item)}
                                  >
                                    Revoke
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* UX task D: revoke confirmation with the consent type's defined consequence */}
      {revokeItem && (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="bg-white rounded-lg shadow-lg" style={{ maxWidth: 460, width: '92%' }}>
            <div className="p-4">
              <h5 className="mb-2 fw-bold" style={{ fontSize: 15 }}>
                Revoke {revokeItem.consent || 'this consent'}?
              </h5>
              <p className="text-muted mb-1" style={{ fontSize: 12.5 }}>
                {REVOKE_CONSEQUENCE[revokeItem.key || ''] || 'This consent will be revoked.'}
              </p>
              <p className="text-muted mb-0" style={{ fontSize: 11.5 }}>
                Your revocation is recorded with the date and time. You can activate the consent
                again from this page at any time.
              </p>
            </div>
            <div className="d-flex justify-content-end gap-2 px-4 pb-4">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRevokeItem(null)}>
                Keep consent
              </button>
              <button type="button" className="btn btn-danger btn-sm" onClick={confirmRevoke}>
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flow-1.pdf point 1: two-step consequence flow for Privacy Consent / Platform Terms.
          Step 1 = platform not usable; Step 2 = data retention + AGREE/DISAGREE. AGREE deactivates. */}
      {deactivateItem && (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="bg-white rounded-lg shadow-lg" style={{ maxWidth: 480, width: '92%' }}>
            <div className="p-4">
              <h5 className="mb-3 fw-bold" style={{ fontSize: 15 }}>
                Revoke {deactivateItem.consent || 'this consent'}?
              </h5>
              {deactivateStep === 1 ? (
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  <strong>If you do this, the platform cannot be used.</strong> {deactivateItem.consent || 'This consent'}{' '}
                  is required to use Facilon. Revoking it will make your account inoperative and you will
                  not be able to sign in.
                </p>
              ) : (
                <>
                  <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                    Your personal data will be <strong>retained as required by law</strong>.
                  </p>
                  <p className="text-muted mb-0" style={{ fontSize: 12.5 }}>
                    On agreeing, your account is made inoperative, a confirmation is emailed to you and to
                    Facilon, and you will be signed out. To reactivate your account later, please contact Facilon.
                  </p>
                </>
              )}
            </div>
            <div className="d-flex justify-content-end gap-2 px-4 pb-4">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={deactivating}
                onClick={() => { setDeactivateItem(null); setDeactivateStep(1); }}
              >
                {deactivateStep === 1 ? 'Keep consent' : 'Disagree'}
              </button>
              {deactivateStep === 1 ? (
                <button type="button" className="btn btn-danger btn-sm" onClick={() => setDeactivateStep(2)}>
                  Continue
                </button>
              ) : (
                <button type="button" className="btn btn-danger btn-sm" disabled={deactivating} onClick={confirmDeactivate}>
                  {deactivating ? 'Processing…' : 'Agree'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SowAgreementModal
        show={showSowModal}
        busy={busyKey === 'sow'}
        onClose={() => setShowSowModal(false)}
        onAgree={handleSowAgree}
      />
    </div>
  );
};

export default MyConsents;
